import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip, Checkbox, FormControlLabel
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import companiaService, { Compania } from '../../services/companiaService';
import { persistCompaniaDefaultCompras, readCompaniaDefaultCompras } from '../../utils/companiaDefaultCompras';

const Companias: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [companias, setCompanias] = useState<Compania[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [_loading, setLoading] = useState(false);
  const [idDefaultCompras, setIdDefaultCompras] = useState<string | null>(() => readCompaniaDefaultCompras());

  const handleToggleDefaultCompras = (compania: Compania) => {
    const id = String(compania.idCompania ?? '').trim();
    const nuevo = idDefaultCompras === id ? null : id;
    persistCompaniaDefaultCompras(nuevo);
    setIdDefaultCompras(nuevo);
    enqueueSnackbar(
      nuevo
        ? `${compania.nombre} quedó como predeterminada para Facturas de Compra en este navegador`
        : 'Se quitó la compañía predeterminada para Facturas de Compra',
      { variant: 'success' },
    );
  };

  const fetchCompanias = async (pageNum: number = page, pageSize: number = rowsPerPage, search: string = searchTerm) => {
    try {
      setLoading(true);
      const data = await companiaService.getCompaniasPaged({
        pageNumber: pageNum + 1,
        pageSize: pageSize,
        searchTerm: search
      });
      setCompanias(data.items);
      setTotalCount(data.totalCount);
    } catch (error: any) {
      console.error('Error al cargar compañías:', error);
      enqueueSnackbar('Error al cargar compañías', { variant: 'error' });
      setCompanias([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanias();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchCompanias(0, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchCompanias(newPage, rowsPerPage, searchTerm);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchCompanias(0, newRowsPerPage, searchTerm);
  };

  const handleDelete = async (idCompania: string) => {
    if (window.confirm('¿Está seguro de eliminar esta compañía?')) {
      try {
        await companiaService.deleteCompania(idCompania);
        enqueueSnackbar('Compañía eliminada exitosamente', { variant: 'success' });
        fetchCompanias();
      } catch (error: any) {
        console.error('Error al eliminar compañía:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar compañía', { variant: 'error' });
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Compañías
            </Typography>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/sistema/companias/nuevo')}>
              Nueva Compañía
            </Button>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por ID, nombre o CUIT..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {companias.map((compania) => (
                <Card key={compania.idCompania} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {compania.nombre}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID: {compania.idCompania}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      CUIT: {compania.cuit || '-'}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={idDefaultCompras === String(compania.idCompania ?? '').trim()}
                          onChange={() => handleToggleDefaultCompras(compania)}
                        />
                      }
                      label="Predeterminada en Facturas de Compra"
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => navigate(`/sistema/companias/${compania.idCompania}`)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(compania.idCompania)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>CUIT</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Domicilio</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      <Tooltip title="Compañía que se toma por defecto en Facturas de Compra (se guarda en este navegador)">
                        <span>Pred. compras</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {companias.map((compania) => (
                    <TableRow key={compania.idCompania} hover>
                      <TableCell>{compania.idCompania}</TableCell>
                      <TableCell>{compania.nombre}</TableCell>
                      <TableCell>{compania.cuit || '-'}</TableCell>
                      <TableCell>{compania.domicilio || '-'}</TableCell>
                      <TableCell align="center">
                        <Checkbox
                          size="small"
                          checked={idDefaultCompras === String(compania.idCompania ?? '').trim()}
                          onChange={() => handleToggleDefaultCompras(compania)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => navigate(`/sistema/companias/${compania.idCompania}`)} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(compania.idCompania)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Companias;
